import React, { useMemo, useState, useEffect } from "react";
import moment from "moment";
import TableContainer from "./TableContainer";
import PatientLoader from "../../../common/PatientLoader";
import "./BillingDataTableStyles.css";
import RiskPill from "../../../common/RiskPill";
import { useSelector } from "react-redux";
import { getFullName, notify, reportStatus } from "../../../../util/helpers";
import { useNavigate } from "react-router-dom";

const BillingDataTable = ({ date }) => {
  const navigate = useNavigate();
  const { loading, patientData, billableBHIPatients } = useSelector(
    (state) => state.patient
  );
  const { staffData } = useSelector((state) => state.app);
  const { bhiLoading, allBHIPatients } = useSelector((state) => state.bhi);
  const [localLoading, setLocalLoading] = useState(false);

  const handleReportRequest = (mode, id) => {
    if (reportStatus(billableBHIPatients, id) === "not ready") {
      notify("This report is not ready yet!", "info");
      return;
    }

    if (mode === "view") {
      navigate(`/billing/reports/${id.toString()}/${date?.value}`);
    } else if (mode === "download") {
      //* Using window.open() to be able to close programatically once download is complete
      window.open(
        `/billing/reports/${id.toString()}/${date?.value}/?download=true`,
        "_blank",
        ""
      );
    }
  };
  const getClinicianName = (id) => {
    let staff = "Unknown";
    staff = staffData.filter((e) => {
      if (e.id === id) {
        return e;
      }
    });
    if (staff !== "Unknown" && staff.length > 0) {
      staff = getFullName(staff[0]);
    }

    return staff;
  };

  useEffect(() => {
    if (loading || bhiLoading) {
      setLocalLoading(true);
    }
    if (!loading && !bhiLoading) {
      setLocalLoading(false);
    }
  }, [loading, bhiLoading]);

  let renderColumns = [
    {
      Header: "Name",
      accessor: (d) => getFullName(d),
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>{cell.value}</span>
        );
      },
    },
    {
      Header: "Primary Provider",
      accessor: "clinician",
      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {getClinicianName(cell.value)}
          </span>
        );
      },
    },
    {
      Header: "Created",
      accessor: "created",
      sortType: (a, b) => {
        if (a === null || a === undefined || a === "" || a === "Not Available")
          return 1; // place null values at the bottom
        if (b === null || b === undefined || b === "" || b === "Not Available")
          return -1;
        return (
          new Date(b?.values?.last_active) - new Date(a?.values?.last_active)
        );
      },

      Cell: ({ cell }) => {
        return (
          <span style={{ textTransform: "capitalize" }}>
            {cell.value
              ? moment(cell.value).format("MMM Do YYYY, h:mma")
              : "Not Available"}
          </span>
        );
      },
    },
    {
      Header: "Report Status",
      accessor: (d) => d.id,
      disableSortBy: true,
      Cell: ({ cell }) => {
        const status = reportStatus(billableBHIPatients, cell.value);
        return (
          <RiskPill
            styles={{ width: "fit-content", gap: "5px" }}
            type={status}
          />
        );
      },
    },
    {
      Header: "BHI Enrolled",
      accessor: (d) => d?.id,
      Cell: ({ cell }) => {
        const getEnrollmentDate = () => {
          let enrollment_date = "Not Available";
          if (allBHIPatients?.length > 0) {
            const target = allBHIPatients.find((ele) => {
              if (ele.id === cell.value) {
                return ele;
              }
            });
            if (target) {
              enrollment_date = moment(
                new Date(target?.enrollment_date)
              ).format("MM/DD/YYYY");
            }
          }

          return enrollment_date;
        };

        const enrollment_date = getEnrollmentDate();
        return <span>{enrollment_date}</span>;
      },
    },
    {
      Header: "",
      accessor: "id",
      disableSortBy: true,
      Cell: ({ cell }) => {
        return (
          <div className="d-flex" style={{ gap: "10px" }}>
            <button
              onClick={() => handleReportRequest("view", cell.value)}
              className="primary-btn-small"
            >
              View
            </button>
            <button
              onClick={() => handleReportRequest("download", cell.value)}
              className="primary-btn-small"
            >
              Download
            </button>
          </div>
        );
      },
    },
  ];

  const columns = useMemo(() => renderColumns, [localLoading]);

  return (
    <>
      {localLoading ? (
        <PatientLoader />
      ) : (
        <TableContainer
          columns={columns}
          data={patientData}
          loading={localLoading}
        />
      )}
    </>
  );
};

export default BillingDataTable;
